import { Injectable, NgZone, Inject } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { AppConfigService } from "src/app/services/app-config.service";
import { MESSAGES } from "src/app/shared/constants/messages.constant";
import { ComparisonNormalizer } from "src/app/shared/utils/comparison-normalizer.util";
import { RetryService } from "./retry.service";
import { ExtractionService, TranscriptResult } from "./extraction.service";
import { OKTA_AUTH } from "@okta/okta-angular";
import OktaAuth from "@okta/okta-auth-js";
import { EvaluationService } from "./evaluation.service";
import { EquivalencyService } from "./equivalency.service";

interface WebSocketMessage {
  status?: string;
  state?: string;
  node?: string;
  checkpoint_type?: string;
  type?: string;
  session_key?: string;
  output?: unknown;
  data?: unknown;
}

@Injectable({
  providedIn: "root",
})
export class DataService {
  private socket?: WebSocket;
  private readonly metaDataWebsocketUrl: string = "";
  private readonly showConsoleLogs: boolean = false;
  private combinedId: string = "";
  private readonly stateSubject = new BehaviorSubject<string>("");
  public readonly state$: Observable<string> = this.stateSubject.asObservable();

  constructor(
    private readonly extractionService: ExtractionService,
    private readonly evaluationService: EvaluationService,
    private readonly equivalencyService: EquivalencyService,
    private readonly envService: AppConfigService,
    private readonly zone: NgZone,
    private readonly retryService: RetryService,
    @Inject(OKTA_AUTH) private readonly oktaAuth: OktaAuth,
  ) {
    this.metaDataWebsocketUrl = this.envService.mmpMetadataWebsocketUrl;
    this.showConsoleLogs = this.envService.showConsoleLogs;
  }

  startWebSocket(combinedId: string): void {
    this.combinedId = combinedId;
    this.extractionService.getTranscriptMap().clear();
    this.socket = new WebSocket(this.metaDataWebsocketUrl);

    this.socket.onopen = async () => {
      const accessToken = this.oktaAuth.getAccessToken();

      this.socket.send(
        JSON.stringify({
          type: "auth",
          token: accessToken,
        }),
      );

      const payload = {
        combined_id: combinedId,
      };

      this.socket?.send(JSON.stringify(payload));

      const intervalId = setInterval(() => {
        if (this.socket?.readyState === WebSocket.OPEN) {
          this.socket.send(
            JSON.stringify({
              type: MESSAGES.WEBSOCKET.HANDSHAKE_TYPE,
              text: MESSAGES.WEBSOCKET.UPLOAD_HANDSHAKE_MESSAGE,
            }),
          );
          if (this.showConsoleLogs) {
            console.log(MESSAGES.WEBSOCKET.FIXED_MESSAGE_SENT);
          }
          return;
        }

        if (this.showConsoleLogs) {
          console.log(MESSAGES.WEBSOCKET.HANDSHAKE_STOPED);
        }
        clearInterval(intervalId);
      }, 10000);
    };

    this.socket.onmessage = (event) => {
      this.zone.run(() => this.handleMessage(event));
    };

    this.socket.onerror = (error) => {
      if (this.showConsoleLogs) {
        console.error("WebSocket error:", error);
      }
    };

    this.socket.onclose = (event) => {
      const currentValue = this.getTranscriptMapValues();
      if (event.wasClean) {
        this.handleCleanClose(event, currentValue);
      } else {
        this.handleUnexpectedClose(event, currentValue);
      }
    };
  }

  disconnect(): void {
    this.socket?.close();
    this.stateSubject.next("");
  }

  getState(): string {
    return this.stateSubject.getValue();
  }

  private handleCleanClose(
    event: CloseEvent,
    currentValue: TranscriptResult[],
  ): void {
    if (this.showConsoleLogs) {
      console.log(MESSAGES.WEBSOCKET.CLOSED_CLEAN, event.code, event.reason);
    }

    if (
      ComparisonNormalizer.string(event.reason) === "keepalive ping timeout"
    ) {
      this.retryService.checkRetryRequired(currentValue, "extraction");
    }
  }

  private handleUnexpectedClose(
    event: CloseEvent,
    currentValue: TranscriptResult[],
  ): void {
    if (this.showConsoleLogs) {
      console.warn(
        MESSAGES.WEBSOCKET.CLOSED_UNEXPECTED,
        event.code,
        event.reason,
      );
    }

    if (currentValue.length === 0) {
      if (this.showConsoleLogs) {
        console.log("Websocket closed unexpectedly, retry upload ws");
      }
      this.metaDataRetry();
      return;
    }

    this.metaDataRetry();
  }

  private handleMessage(event: MessageEvent): void {
    const data = this.parseMessage(event);
    const sessionKey = this.getSessionKey(data);

    this.handleStartedMessage(data, sessionKey);
    this.handleExtractionMessage(data, sessionKey);
    this.handleTranscriptVersionMessage(data);
    this.handlePreEvaluationMessage(data, sessionKey);
    this.handleCourseMatchingMessage(data, sessionKey);
    this.handleErrorMessage(data);
    this.handleCompletedMessage(data, sessionKey);
  }

  private parseMessage(event: MessageEvent): WebSocketMessage {
    return JSON.parse(event.data) as WebSocketMessage;
  }

  private getSessionKey(data: WebSocketMessage): string {
    return data.session_key ?? "";
  }

  private handleStartedMessage(
    data: WebSocketMessage,
    sessionKey: string,
  ): void {
    if (ComparisonNormalizer.string(data.status) !== "started") {
      return;
    }

    this.extractionService.processStartedStatus(data.status ?? "", sessionKey);
    this.evaluationService.initializeEvaluationMapPending(sessionKey);
    this.equivalencyService.initializeEquivalencyMapPending(sessionKey);
    this.stateSubject.next("extraction");
  }

  private handleExtractionMessage(
    data: WebSocketMessage,
    sessionKey: string,
  ): void {
    if (ComparisonNormalizer.string(data.state) !== "extraction") {
      return;
    }

    this.handleExtractionOutputs(data, sessionKey);
    this.handleExtractionCheckpoint(data, sessionKey);
    this.handleExtractionFinalSummary(data);
  }

  private handleExtractionOutputs(
    data: WebSocketMessage,
    sessionKey: string,
  ): void {
    if (ComparisonNormalizer.string(data.node) !== "extraction_outputs") {
      return;
    }

    this.extractionService.processExtractionOutput(data, sessionKey);
  }

  private handleExtractionCheckpoint(
    data: WebSocketMessage,
    sessionKey: string,
  ): void {
    if (
      ComparisonNormalizer.string(data.checkpoint_type) !==
      "extraction_checkpoint"
    ) {
      return;
    }

    this.extractionService.processExtractionCheckpoint(data, sessionKey);
  }

  private handleExtractionFinalSummary(data: WebSocketMessage): void {
    if (ComparisonNormalizer.string(data.type) !== "final_summary") {
      return;
    }

    this.extractionService.setSavedHiddenIndexesEvaluation();
    this.extractionService.processFinalSummary(data);
  }

  private handleTranscriptVersionMessage(data: WebSocketMessage): void {
    if (ComparisonNormalizer.string(data.type) !== "transcript_version") {
      return;
    }

    this.extractionService.processDuplicate(data);
  }

  private handlePreEvaluationMessage(
    data: WebSocketMessage,
    sessionKey: string,
  ): void {
    if (ComparisonNormalizer.string(data.state) !== "pre_evaluation") {
      return;
    }

    this.handlePreEvaluationOutputs(data, sessionKey);
    this.handlePreEvaluationCheckpoint(data, sessionKey);
    this.handlePreEvaluationFinalSummary(data);
  }

  private handlePreEvaluationOutputs(
    data: WebSocketMessage,
    sessionKey: string,
  ): void {
    if (ComparisonNormalizer.string(data.node) !== "pre_evaluation_outputs") {
      return;
    }

    this.evaluationService.confirmEvaluationMapEntry(sessionKey);
    this.evaluationService.processPreEvaluationOutput(data, sessionKey);
    this.stateSubject.next("evaluation");
  }

  private handlePreEvaluationCheckpoint(
    data: WebSocketMessage,
    sessionKey: string,
  ): void {
    if (
      ComparisonNormalizer.string(data.checkpoint_type) !==
      "pre_evaluation_checkpoint"
    ) {
      return;
    }

    this.evaluationService.processPreEvaluationCheckpoint(data, sessionKey);
  }

  private handlePreEvaluationFinalSummary(data: WebSocketMessage): void {
    if (ComparisonNormalizer.string(data.type) !== "final_summary") {
      return;
    }

    this.evaluationService.setSavedHiddenIndexesEquivalency();
    this.evaluationService.processFinalSummary(data);
  }

  private handleCourseMatchingMessage(
    data: WebSocketMessage,
    sessionKey: string,
  ): void {
    if (ComparisonNormalizer.string(data.state) !== "course_matching") {
      return;
    }

    this.handleCourseMatchingOutputs(data, sessionKey);
    this.handleCourseMatchingCheckpoint(data, sessionKey);
    this.handleCreditBreakdown(data);
    this.handleCourseMatchingFinalSummary(data);
  }

  private handleCourseMatchingOutputs(
    data: WebSocketMessage,
    sessionKey: string,
  ): void {
    if (ComparisonNormalizer.string(data.node) !== "course_matching_outputs") {
      return;
    }

    this.equivalencyService.confirmEquivalencyMapEntry(sessionKey);
    this.equivalencyService.processCourseMatchingOutput(data, sessionKey);
    this.stateSubject.next("equivalency");
  }

  private handleCourseMatchingCheckpoint(
    data: WebSocketMessage,
    sessionKey: string,
  ): void {
    if (
      ComparisonNormalizer.string(data.checkpoint_type) !==
      "course_matching_checkpoint"
    ) {
      return;
    }

    this.equivalencyService.processCourseMatchingCheckpoint(data, sessionKey);
  }

  private handleCreditBreakdown(data: WebSocketMessage): void {
    if (ComparisonNormalizer.string(data.type) !== "credit_breakdown") {
      return;
    }

    this.equivalencyService.processCreditBreakdown(data);
  }

  private handleCourseMatchingFinalSummary(data: WebSocketMessage): void {
    if (ComparisonNormalizer.string(data.type) !== "final_summary") {
      return;
    }

    this.equivalencyService.processFinalSummary(data);
  }

  private handleErrorMessage(data: WebSocketMessage): void {
    if (ComparisonNormalizer.string(data.state) !== "error") {
      return;
    }

    this.extractionService.handleErrorStatus(data.status ?? "");
  }

  private handleCompletedMessage(
    data: WebSocketMessage,
    sessionKey: string,
  ): void {
    if (ComparisonNormalizer.string(data.status) !== "completed") {
      return;
    }

    this.evaluationService.resolvePendingEvaluationMapEntry(sessionKey);
    this.equivalencyService.resolvePendingEquivalencyMapEntry(sessionKey);
    this.handleCompletedStatus(data.status ?? "");
  }

  public handleCompletedStatus(status: string): void {
    if (ComparisonNormalizer.string(status) !== "completed") {
      return;
    }

    const allCompleted = Array.from(
      this.extractionService.getTranscriptMap().values(),
    ).every(
      (transcript) =>
        ComparisonNormalizer.string(transcript.status) === "completed",
    );

    if (allCompleted) {
      if (this.showConsoleLogs) {
        console.log("All transcripts are completed. Closing WebSocket...");
      }
      this.disconnect();
      return;
    }

    if (this.showConsoleLogs) {
      console.log(MESSAGES.RETRY.RETRY_REQUIRED);
    }
    this.disconnect();
    const currentValue = this.getTranscriptMapValues();
    this.retryService.checkRetryRequired(currentValue, "extraction");
  }

  private getTranscriptMapValues(): TranscriptResult[] {
    return Array.from(this.extractionService.getTranscriptMap().values());
  }

  private metaDataRetry(): void {
    this.retryService.showAgentRetryConfirmationDialog().then((response) => {
      if (ComparisonNormalizer.boolean(response)) {
        this.startWebSocket(this.combinedId);
      }
    });
  }

  public disconnectRetry(): void {
    this.retryService.disconnect();
  }
}
